import { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import React from "react";
import { useFrame, useGraph } from "@react-three/fiber";
import { clientAtom, hasTurnAtom, selectionAtom, showFinishMovesAtom, teamsAtom, tilesAtom, turnAtom } from "../GlobalState";
import * as THREE from "three";
import BackdoToken from "../moveTokens/BackdoToken";
import MoToken from "../moveTokens/MoToken";
import YootToken from "../moveTokens/YootToken";
import GulToken from "../moveTokens/GulToken";
import GeToken from "../moveTokens/GeToken";
import DoToken from "../moveTokens/DoToken";
import { animated, useSpring } from "@react-spring/three";
import { useAnimationPlaying } from "../hooks/useAnimationPlaying";
import { useGLTF } from "@react-three/drei";
import { SkeletonUtils } from "three-stdlib";
import { useGameHelpers } from "../stores/useGameHelpers.js";
import MeshColors from "./MeshColors.jsx";
import PointerCommand from "../meshes/PointerCommand.jsx";
import { hasValidMoveBoard } from "../gameLogic/rules.js";
import useStarRoll from "../shader/starRoll/StarRoll";
import useSelect from "../hooks/useSelect";
import gsap from "gsap";
import { trackEvent } from '../api';

export default function Tile({ 
  position=[0,0,0], 
  rotation=[0,0,0], 
  scale=1, 
  tile=null,
  mesh,
  legalTileInfo, // If the key is not in the object, it's undefined
  pathNum,
  interactive=false
}) {

  const selection = useAtomValue(selectionAtom);
  const hasTurn = useAtomValue(hasTurnAtom)
  const tiles = useAtomValue(tilesAtom)
  const teams = useAtomValue(teamsAtom)
  const client = useAtomValue(clientAtom)
  const turn = useAtomValue(turnAtom)
  const animationPlaying = useAnimationPlaying()
  const showFinishMoves = useAtomValue(showFinishMovesAtom)

  const group = useRef()
  const wrapperMat = useRef();
  const wrapper = useRef();
  const meshGroup = useRef();
  const flashMaterialsRef = useRef([])
  const [RollStar] = useStarRoll()
  const rollStarRef = useRef(RollStar)
  const registerTileAnimation = useGameHelpers(s => s.registerTileAnimation)
  const unregisterTileAnimation = useGameHelpers(s => s.unregisterTileAnimation)

  useEffect(() => {
    rollStarRef.current = RollStar
  }, [RollStar])

  useEffect(() => {
    registerTileAnimation({
      tile,
      context: {
        meshGroupRef: meshGroup,
        groupRef: group,
        flashMaterialsRef,
        rollStar: (...args) => rollStarRef.current(...args),
      },
    })

    return () => {
      unregisterTileAnimation({ tile })
    }
  }, [registerTileAnimation, unregisterTileAnimation, tile])

  useEffect(() => {
    return () => {
      if (meshGroup.current) {
        gsap.killTweensOf(meshGroup.current.scale)
      }
      flashMaterialsRef.current.forEach(m => gsap.killTweensOf(m))
    }
  }, [])

  function handlePointerEnter(event) {
    event.stopPropagation();
    // Doesn't change in Chrome
    // Browser runs on compatibility mode in prod
    document.body.style.cursor = "pointer";
  }

  function handlePointerLeave(event) {
    event.stopPropagation();
    document.body.style.cursor = "default";
  }

  const handlePointerDown = useSelect({
    targetTile: tile,
    canStartSelection: () => {
      const pieces = tiles[tile];
      return pieces.length > 0 && pieces[0].team === client.team;
    },
    getSelectionData: () => {
      const pieces = tiles[tile];
      return {
        tile,
        pieces,
        history: pieces[0].history,
        team: client.team,
      };
    },
    isLegalMoveTarget: () => Boolean(legalTileInfo),
  });

  function hasValidMove(team) {
    const moves = teams[team].moves
    for (const move in moves) {
      if (parseInt(move) !== 0 && moves[move] > 0) {
        return true;
      }
    }
    return false;
  }

  const hasMovablePiece = selection === null 
  && tiles[tile].length > 0 
  && tiles[tile][0].team === client.team 
  && hasTurn && hasValidMove(client.team)
  && !animationPlaying
  
  const { wrapperScale } = useSpring({
    wrapperScale: (!showFinishMoves && (selection != null && legalTileInfo) || hasMovablePiece) ? 1 : 0, // want the animation to start again when status changes
  })

  const baseWrapperScale = 0.9
  const wrapperIncreaseScaleFactor = 0.02
  useFrame((state) => {
    const time = state.clock.elapsedTime;
    if (hasMovablePiece) {
      if (Math.floor(state.clock.elapsedTime) % 2 == 0) {
        wrapperMat.current.opacity = 0.3
        if (tiles[tile][0].team === 0) {
          wrapperMat.current.color = new THREE.Color('#EA5E5E')
        } else {
          wrapperMat.current.color = new THREE.Color('turquoise')
        }
      } else {
        wrapperMat.current.opacity = 0.3
        wrapperMat.current.color = new THREE.Color('grey')
      }
      wrapper.current.scale.x = Math.cos(time * 3) * wrapperIncreaseScaleFactor + baseWrapperScale;
      wrapper.current.scale.y = Math.cos(time * 3) * wrapperIncreaseScaleFactor + baseWrapperScale;
      wrapper.current.scale.z = Math.cos(time * 3) * wrapperIncreaseScaleFactor + baseWrapperScale;
    } else if (selection != null && legalTileInfo) {
      if (turn.team === 0) {
        wrapperMat.current.color.setHSL(Math.cos(time * 3) * 0.02 + 0.03, 0.8, 0.5);
      } else {
        wrapperMat.current.color.setHSL(Math.cos(time * 3) * 0.06 + 0.55, 1, 0.3);
      }
      wrapperMat.current.opacity = 0.3;
      wrapper.current.scale.x = Math.cos(time * 3) * wrapperIncreaseScaleFactor + baseWrapperScale;
      wrapper.current.scale.y = Math.cos(time * 3) * wrapperIncreaseScaleFactor + baseWrapperScale;
      wrapper.current.scale.z = Math.cos(time * 3) * wrapperIncreaseScaleFactor + baseWrapperScale;
    } else {
      wrapperMat.current.opacity = 0;
    }
  })

  function PathNumHelper({pathNum}) {
    function shiftPosition({ tile }) {
      const positionTopRight = [1, 2.3, 0]
      const positionTopLeft = [-1, 2.3, 0]
      const positionBottomRight = [1, 2.3, 2]
      const positionBottomLeft = [-1, 2.3, 2]
      if (tile === 1 || tile === 2 || tile === 3 || tile === 4) {
        return positionBottomRight
      } else if (tile === 0 || tile === 5 || tile === 6 || tile === 7 || tile === 8 || tile === 9) {
        return positionTopRight
      } else if (tile === 10 || tile === 11 || tile === 12 || tile === 13 || tile === 14) {
        return positionTopRight
      } else if (tile === 15 || tile === 16 || tile === 17 || tile === 18 || tile === 19) {
        return positionTopRight
      } else if (tile === 20 || tile === 21 || tile === 22 || tile === 23 || tile === 24) {
        return positionTopRight
      } else if (tile === 25 || tile === 26 || tile === 27 || tile === 28) {
        return positionTopRight
      }
    }
    const Move = ({ position }) => {
      return <>
        { pathNum === -1 && <BackdoToken position={position} scale={0.8} rotation={[0, Math.PI/2, 0]}/>}
        { pathNum === 1 && <DoToken position={position} scale={0.8} rotation={[0, Math.PI/2, 0]}/>}
        { pathNum === 2 && <GeToken position={position} scale={0.8} rotation={[0, Math.PI/2, 0]}/>}
        { pathNum === 3 && <GulToken position={position} scale={0.8} rotation={[0, Math.PI/2, 0]}/>}
        { pathNum === 4 && <YootToken position={position} scale={0.8} rotation={[0, Math.PI/2, 0]}/>}
        { pathNum === 5 && <MoToken position={position} scale={0.8} rotation={[0, Math.PI/2, 0]}/>}
      </>
    }

    return <>
      <Move position={shiftPosition({tile})}/>
    </>
  }

  function Pointer() {
    const animationPlaying = useAnimationPlaying()
    const tokenChoices = useGameHelpers(s => s.tokenChoices)
    const tokenChoice = useGameHelpers(s => s.tokenChoice)

    return !animationPlaying && 
    tokenChoices[tokenChoice] === tile && hasValidMoveBoard(teams[turn.team].moves) && 
    hasTurn &&
    <PointerCommand
      position={[0, 0, 0]}
      rotation={[Math.PI/2, 0, 0]}
      color={MeshColors.yellow}
      scale={1.3}
    />
  }

  return <group position={position} rotation={rotation} scale={scale}>
    <group ref={group}>
      <animated.group scale={wrapperScale}>
        <mesh
          name='wrapper'
          onPointerEnter={(e) => { interactive && handlePointerEnter(e) }}
          onPointerLeave={(e) => { interactive && handlePointerLeave(e) }}
          onPointerDown={(e) => {
            if (interactive) {
              handlePointerDown(e)
            }
          }}
          ref={wrapper}
        >
          <sphereGeometry args={[0.8, 32, 16]} />
          <meshStandardMaterial
            transparent
            opacity={0}
            ref={wrapperMat}
            depthWrite={false}
          />
        </mesh>
      </animated.group>
      <group ref={meshGroup}>
        {mesh}
      </group>
      {/* path num */}
      { !showFinishMoves && pathNum && <PathNumHelper pathNum={pathNum}/> }
      <Pointer/>
    </group>
  </group>
}